import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Tooltip,
} from '@mui/material';
import { Pause, PlayArrowRounded } from '@mui/icons-material';
import BadgeAvatarNew from '@src/components/BadgeAvatarNew';
import { checkVietNam } from '@src/utils/checkCountry';
import { PACKAGE_LEVEL, PACKAGE_TYPE } from '@src/constants/package';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import { TTS_VERSION } from '@src/constants/tts';
import { checkFeaturePermission } from '@src/services/tts';
import actions from '@src/redux/actions';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LogoVbee from '@src/assets/logo/logo-vbee.svg';
import { VOICE_LEVEL, VOICE_PROVIDER } from '@src/constants/voice';
import { COLOR } from '@src/styles/color';
import useUserInfo from '@src/hooks/useTtsUser';
import { StyledVoiceItem } from './index.style';
import ImportantTagNew from '../ImportantTagNew';

const VoiceItem = ({
  voice,
  activeVoiceId,
  isPlaying,
  canUseVoiceCloning,
  usingPackageType,
  savedCurrentTime,
  setSavedCurrentTime,
  onChangePlayAudio,
  onSelectedVoice,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();

  const { openSentences } = useSelector((state) => state.synthesisRequest);
  const { defaultGlobalVoice } = useSelector((state) => state.voice);
  const { usingDubbingPackage } = useSelector((state) => state.user);

  const [progress, setProgress] = useState(0);
  const [showPlayAudio, setShowPlayAudio] = useState(false);
  const audioRef = useRef(new Audio());

  const isVietNam = checkVietNam();

  useEffect(() => {
    const audio = audioRef.current;

    if (audio.src !== voice.demo) {
      setSavedCurrentTime(0);
      audio.src = voice.demo;
      audio.currentTime = 0;
      setProgress(0);
    }

    const handleTimeUpdate = () => {
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const handleEnded = () => {
      setProgress(0);
      onChangePlayAudio(voice);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    if (isPlaying) {
      audio.currentTime = savedCurrentTime;
      audio.play();
    } else {
      setSavedCurrentTime(!audio.src ? 0 : audio.currentTime);
      audio.pause();
    }

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
    };
  }, [voice.demo, isPlaying]);

  const isDefaultGlobalVoice =
    !isVietNam &&
    usingPackage?.level === PACKAGE_LEVEL.BASIC &&
    voice?.code === defaultGlobalVoice;

  const canUseVoice = () => {
    const isVoiceCloning =
      voice?.provider && voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING;
    if (isVoiceCloning && !canUseVoiceCloning) return false;
    if (!voice?.features?.length) return true;

    const usingPackageByType =
      usingPackageType === PACKAGE_TYPE.DUBBING ? ttsUser?.dubbing : ttsUser;

    if (!usingDubbingPackage && isDefaultGlobalVoice) return true;

    return voice.features.every((item) =>
      checkFeaturePermission(usingPackageByType?.features, item),
    );
  };

  const handleCloseNotificationDialog = () =>
    dispatch(
      actions.dialog.displayDialog({
        notificationDialog: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const handleSelectedVoice = () => {
    if (voice?.version === TTS_VERSION.EMPHASIS && openSentences) {
      dispatch(
        actions.dialog.displayDialog({
          notificationDialog: {
            name: 'cannotUseVoice',
            title: t('cannotUseVoice'),
            description: t('cannotUseVoiceDes'),
            variant: 'warning',
            open: true,
            actionComponent: (
              <Button
                variant="contained"
                onClick={handleCloseNotificationDialog}
              >
                {t('understood')}
              </Button>
            ),
          },
        }),
      );
      return;
    }

    if (canUseVoice()) {
      onSelectedVoice(voice);
    } else {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('limitedFeatureDesc', { feature: t('useThisVoice') }),
        }),
      );
    }
  };

  const handleChangePlayAudio = (e) => {
    e.stopPropagation();
    setProgress(0);
    onChangePlayAudio(voice);
  };
  const [tagName, tagIcon, color, background] = useMemo(() => {
    if (voice?.beta) {
      return [
        t('newVoice'),
        AutoAwesomeIcon,
        COLOR.blueV2,
        'rgba(0, 122, 255, 0.08)',
      ];
    }

    let localTagName = '';
    let localTagIcon = '';
    let localColor = '';
    let localBackground = 'rgba(255, 149, 0, 0.16)';

    if (voice?.level === VOICE_LEVEL.PREMIUM) {
      localTagName = t(voice.level);
      localTagIcon = EmojiEventsIcon;
      localColor = COLOR.alert;
    }

    if (openSentences && voice?.version === TTS_VERSION.EMPHASIS) {
      localBackground = COLOR.lightText;
    }

    return [localTagName, localTagIcon, localColor, localBackground];
  }, [voice, openSentences]);

  const styles =
    voice?.styles?.length > 0
      ? `${voice.styles.map((item) => t(item)).join(' • ')}`
      : '';
  return (
    <StyledVoiceItem
      className="voice-item"
      disabledVoice={openSentences && voice?.version === TTS_VERSION.EMPHASIS}
      active={voice.id === activeVoiceId}
      playingAudio={voice.playStatus}
      onClick={handleSelectedVoice}
      aria-label={`${voice.name} voice`}
      onMouseEnter={() => setShowPlayAudio(true)}
      onMouseLeave={() => setShowPlayAudio(false)}
    >
      {tagName && (
        <ImportantTagNew
          tag={tagName}
          top="8px"
          left="8px"
          borderRadius="8px"
          bgColor={background}
          colorText={color}
          tagIcon={tagIcon}
          color={
            voice.id === activeVoiceId ? COLOR.black.default : COLOR.darkBlue
          }
        />
      )}
      <div className="voice-info">
        <div
          className={`audio-player ${
            showPlayAudio || isPlaying ? 'avatar' : ''
          }`}
        >
          <div className="avatar-wrapper">
            <Box position="relative" display="inline-flex">
              {isPlaying && (
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={75}
                    thickness={3}
                    sx={{
                      position: 'absolute',
                      color: 'rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={progress}
                    size={75}
                    thickness={3}
                    sx={{
                      color: '#FFD700',
                      '& .MuiCircularProgress-svg': {
                        strokeLinecap: 'round',
                      },
                    }}
                  />
                </Box>
              )}
              <Box
                top={0}
                left={0}
                bottom={0}
                right={0}
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <BadgeAvatarNew
                  img={voice.roundImage}
                  smallImg={voice.language.roundImage}
                  type="image"
                />

                {(showPlayAudio || isPlaying) && (
                  <IconButton
                    onClick={handleChangePlayAudio}
                    aria-label="play"
                    className="play-button"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {isPlaying ? (
                      <Pause className="play-icon" fontSize="large" />
                    ) : (
                      <PlayArrowRounded
                        className="play-icon"
                        fontSize="large"
                      />
                    )}
                  </IconButton>
                )}
              </Box>
            </Box>
          </div>
        </div>

        <div className="detail-voice">
          <Typography variant="h6" className="voice-name">
            {voice.name}
            <Tooltip arrow title={t('exclusiveVoice')} placement="right">
              <img
                style={{ height: '20px' }}
                src={LogoVbee}
                alt=""
                className="img-vbee"
              />
            </Tooltip>
          </Typography>
          <Typography variant="subtitle2" className="features">
            {styles}
          </Typography>
        </div>
      </div>
    </StyledVoiceItem>
  );
};

export default VoiceItem;
